import clsx from 'clsx';
import { useMemo, useState } from 'react';

import { Message } from '@/services/types/artifact-types';

import { AttachmentList } from './AttachmentList';
import { MessageDetails } from './MessageDetails';
import { MessageHeader } from './MessageHeader';

interface MessageItemProps {
  message: Message;
  searchQuery: string;
}

export const MessageItem = ({ message, searchQuery }: MessageItemProps) => {
  const [showDetails, setShowDetails] = useState(false);

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelativeTime = (dateString: string) => {
    const now = new Date();
    const messageTime = new Date(dateString);
    const diffInMinutes = Math.floor(
      (now.getTime() - messageTime.getTime()) / (1000 * 60),
    );

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return formatTime(dateString);
  };

  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;

    try {
      const regex = new RegExp(
        `(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
        'gi',
      );
      return text.replace(
        regex,
        '<mark class="bg-yellow-200 text-gray-900 rounded-sm px-0.5">$1</mark>',
      );
    } catch {
      return text;
    }
  };

  const getMessageTypeColor = () => {
    // Color code by direction instead of message type
    return message.is_inbound ? 'border-l-blue-400' : 'border-l-green-400';
  };

  const getSender = () => {
    if (!message.recipient_blob?.recipients?.length) return null;
    const sender = message.recipient_blob.recipients.find(
      (r) => r.role === 'from',
    );
    return sender || message.recipient_blob.recipients[0];
  };

  const getOtherRecipients = () => {
    if (!message.recipient_blob?.recipients?.length) return [];
    const sender = getSender();
    return message.recipient_blob.recipients.filter((r) => r !== sender);
  };

  const hasMetadata = () => {
    return (
      getOtherRecipients().length > 0 ||
      message.source ||
      message.source_timestamp ||
      (message.attachments?.length ?? 0) > 0
    );
  };

  const highlightedContent = useMemo(
    () => highlightText(message.content, searchQuery),
    [message.content, searchQuery],
  );

  const highlightedSubject = useMemo(
    () =>
      message.subject ? highlightText(message.subject, searchQuery) : null,
    [message.subject, searchQuery],
  );

  // ---------- Presentation helpers ----------
  const isSmsLike = ['sms', 'mms'].includes(message.message_type);
  const isEmail = message.message_type === 'email';
  const sender = getSender();

  const containerClasses = clsx(
    'relative group transition-all duration-200',
    isSmsLike && [
      'max-w-md',
      'rounded-xl',
      'p-3',
      'shadow-sm hover:shadow-lg',
      message.is_inbound
        ? [
            'bg-gradient-to-br from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-150',
            'mr-auto',
          ]
        : [
            'bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-150',
            'ml-auto',
          ],
    ],
    !isSmsLike && [
      'bg-gradient-to-r from-white to-gray-50/50 hover:from-gray-50 hover:to-gray-100/50',
      'rounded-r-xl',
      'shadow-sm hover:shadow-lg',
      'p-4',
      getMessageTypeColor(),
      'border-l-4',
    ],
  );

  return (
    <div className={containerClasses}>
      <MessageHeader
        message={message}
        formatRelativeTime={formatRelativeTime}
      />

      {/* Message Content */}
      {message.content && (
        <div
          className={clsx(
            'text-sm leading-relaxed',
            isEmail ? 'pl-8' : '',
            isSmsLike ? 'text-gray-900' : 'text-gray-800',
          )}
        >
          <div dangerouslySetInnerHTML={{ __html: highlightedContent }} />
        </div>
      )}

      {/* Attachment Indicator */}
      <div className={isEmail ? 'pl-8' : ''}>
        <AttachmentList
          attachments={message.attachments || []}
          isExpanded={false}
          showCount={!showDetails}
        />
      </div>

      {/* Expandable Details */}
      <MessageDetails
        message={message}
        isEmail={isEmail}
        isVisible={Boolean(showDetails && hasMetadata())}
      />
    </div>
  );
};
